import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ROLE_LABEL } from "@/lib/format";
import { UserCog, Plus, Pencil, ShieldCheck, Mail, Briefcase } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "owner", "kepala_divisi", "atasan", "staff", "verifikator", "auditor"];
const EMPTY = { name: "", email: "", role: "staff", whatsapp: "", capacity_hours: 40, is_active: true, password: "", feature_overrides: [] };

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [assignPos, setAssignPos] = useState("");
  const [assignType, setAssignType] = useState("utama");
  const [busy, setBusy] = useState(false);

  const isAdmin = ["super_admin", "owner"].includes(user?.role);

  const load = async () => {
    const [u, p, f] = await Promise.all([
      api.get("/org/users"),
      api.get("/org/positions"),
      api.get("/access/features"),
    ]);
    setUsers(u.data);
    setPositions(p.data);
    setFeatures(f.data.features);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setAssignPos(""); setAssignType("utama"); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", role: u.role || "staff", whatsapp: u.whatsapp || "",
      capacity_hours: u.capacity_hours || 40, is_active: u.is_active !== false, password: "",
      feature_overrides: Array.isArray(u.feature_overrides) ? u.feature_overrides : [] });
    setAssignPos(""); setAssignType("utama");
    setOpen(true);
  };

  const toggleOverride = (key) => {
    setForm((f) => {
      const has = f.feature_overrides.includes(key);
      return { ...f, feature_overrides: has ? f.feature_overrides.filter((k) => k !== key) : [...f.feature_overrides, key] };
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nama dan email wajib diisi"); return; }
    if (!editing && (!form.password || form.password.length < 6)) { toast.error("Kata sandi minimal 6 karakter untuk akun baru"); return; }
    setBusy(true);
    try {
      const payload = { ...form, feature_overrides: form.feature_overrides.length ? form.feature_overrides : null };
      if (editing && !form.password) delete payload.password;
      let uid;
      if (editing) { await api.put(`/org/users/${editing.id}`, payload); uid = editing.id; }
      else { const r = await api.post("/org/users", payload); uid = r.data.id; }
      if (assignPos) {
        await api.post("/org/assignments", { user_id: uid, position_id: assignPos, type: assignType });
      }
      toast.success(editing ? "Pengguna diperbarui" : "Akun berhasil dibuat");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
    setBusy(false);
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/org/users/${u.id}`, {
        name: u.name, email: u.email, role: u.role, whatsapp: u.whatsapp,
        capacity_hours: u.capacity_hours || 40, is_active: !(u.is_active !== false),
        feature_overrides: Array.isArray(u.feature_overrides) ? u.feature_overrides : null,
      });
      toast.success((u.is_active !== false) ? "Akun dinonaktifkan" : "Akun diaktifkan");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  if (!isAdmin) return <div className="text-slate-500 py-16 text-center" data-testid="users-forbidden">Hanya Super Admin/Owner yang dapat mengakses Manajemen Pengguna.</div>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><UserCog className="h-7 w-7 text-indigo-500" />Manajemen Pengguna</h1>
          <p className="text-slate-500 text-sm">Buat & kelola akun staff, SPV, manager, serta hak akses fitur</p>
        </div>
        <Button onClick={openNew} data-testid="add-user-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Tambah Akun</Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-left px-3">Email</th>
              <th className="text-left px-3">Peran</th>
              <th className="text-left px-3">Posisi</th>
              <th className="text-center px-3">Hak Akses</th>
              <th className="text-center px-3">Status</th>
              <th className="text-right px-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2.5 font-medium text-slate-800">{u.name}</td>
                <td className="px-3 text-slate-500">{u.email}</td>
                <td className="px-3"><span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-xs font-semibold">{ROLE_LABEL[u.role] || u.role}</span></td>
                <td className="px-3 text-slate-500 text-xs">{(u.positions || []).map((p) => p.title).filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 text-center text-xs">{Array.isArray(u.feature_overrides) && u.feature_overrides.length ? <span className="text-amber-600 font-semibold">Kustom ({u.feature_overrides.length})</span> : <span className="text-slate-400">Default peran</span>}</td>
                <td className="px-3 text-center">
                  <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} disabled={u.id === user?.id} data-testid={`toggle-active-${u.id}`} />
                </td>
                <td className="px-4 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editing ? "Edit Pengguna" : "Tambah Akun Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Peran</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Kapasitas (jam/mgg)</Label><Input type="number" value={form.capacity_hours} onChange={(e) => setForm({ ...form, capacity_hours: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+62..." /></div>
              <div className="space-y-1.5"><Label>{editing ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Kosongkan jika tidak diubah" : "min. 6 karakter"} data-testid="user-password-input" /></div>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-slate-400" />Tempatkan di Posisi (opsional)</Label>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Select value={assignPos} onValueChange={setAssignPos}>
                    <SelectTrigger data-testid="user-position-select"><SelectValue placeholder="Pilih posisi…" /></SelectTrigger>
                    <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} {p.branch_id ? `· ${p.branch_id}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Select value={assignType} onValueChange={setAssignType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utama">Utama</SelectItem>
                    <SelectItem value="rangkap">Rangkap</SelectItem>
                    <SelectItem value="plt">Plt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <Label className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />Akun Aktif</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-active-switch" />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Hak Akses Fitur (opsional)</Label>
              <p className="text-xs text-slate-400">Kosongkan untuk memakai default peran. Centang untuk override khusus pengguna ini.</p>
              <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto zoa-scroll rounded-lg border border-slate-200 p-2">
                {features.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer rounded px-1.5 py-1 hover:bg-slate-50" data-testid={`override-${f.key}`}>
                    <Checkbox checked={form.feature_overrides.includes(f.key)} onCheckedChange={() => toggleOverride(f.key)} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-user-btn">{busy ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
